import { locationConfigs } from '../components/locationPages/locationConfigs.js';
import { getServicePagePaths, SERVICE_PAGE_CONFIGS } from '../components/servicePages/serviceConfigs.js';
import {
  getAllDynamicGuideCanonicalPaths,
  getSpecialGuideBySlug,
} from '../data/specialServiceGuidesRegistry.js';
import { BLOG_POST_LINK_CORPUS } from '../data/blogPostLinkCorpus.js';
import { resolveMarketingSiteOrigin, toAbsoluteSiteUrl } from './absoluteSiteUrl.js';
import { resolveDefaultMarketingOgImagePath } from './companyBranding.js';
import { getEnabledExtraKeywordPages } from './extraKeywordLandingPages.js';
import { HUB_PAGE_SEO_LABELS } from './marketingHubSeo.js';
import { resolveMarketingSeoPreviewCanonicalUrl } from './marketingSeoExport.js';
import {
  resolveMarketingSeoPreviewForPath,
  type MarketingSeoPreviewBranding,
  type MarketingSeoPreviewSource,
} from './marketingSeoPreview.js';
import type { PublicMarketingConfig } from './publicMarketingConfig.js';

const DEFAULT_SERVICE_HERO = '/images/hero-deep-cleaning-kochi.png';

/** Static special landings with dedicated hero assets (matches page components). */
const SPECIAL_STATIC_OG_IMAGES: Record<string, string> = {
  '/bed-bug-treatment-kochi': '/bed-bug-treatment-acrenzel-tools-bubbles.png',
  '/ulv-fogging-disinfection-kochi': '/ulv-cold-fogger-tools-bubbles.png',
};

export type MarketingSitemapPathKind =
  'homepage' | 'hub' | 'service' | 'location' | 'special' | 'guide' | 'blog' | 'keyword';

export type MarketingSitemapPathEntry = {
  path: string;
  kind: MarketingSitemapPathKind;
  label: string;
};

export type MarketingSitemapAuditRow = {
  path: string;
  kind: MarketingSitemapPathKind;
  label: string;
  canonicalUrl: string;
  ogImageUrl: string;
  title: string;
  description: string;
  source: MarketingSeoPreviewSource;
  hasOverride: boolean;
};

function getLocationPagePaths(): string[] {
  const paths = new Set<string>();
  for (const cfg of Object.values(locationConfigs)) {
    paths.add(`/${cfg.slug}`);
  }
  return [...paths].sort();
}

/** All indexable marketing routes (homepage, hub, service, location, special, guides, blog, extra keywords). */
export function listAllMarketingSitemapPaths(config?: PublicMarketingConfig): MarketingSitemapPathEntry[] {
  const entries: MarketingSitemapPathEntry[] = [{ path: '/', kind: 'homepage', label: 'Homepage' }];

  for (const [path, label] of Object.entries(HUB_PAGE_SEO_LABELS)) {
    entries.push({ path, kind: 'hub', label });
  }

  for (const path of getServicePagePaths()) {
    const slug = path.replace(/^\//, '');
    const cfg = SERVICE_PAGE_CONFIGS[slug];
    entries.push({
      path,
      kind: 'service',
      label: cfg?.serviceTitle ?? slug,
    });
  }

  for (const path of getLocationPagePaths()) {
    const cfg = Object.values(locationConfigs).find((item) => `/${item.slug}` === path);
    entries.push({
      path,
      kind: 'location',
      label: cfg?.locationName ? `${cfg.locationName} location` : path,
    });
  }

  for (const path of Object.keys(SPECIAL_STATIC_OG_IMAGES)) {
    entries.push({
      path,
      kind: 'special',
      label: path.replace(/^\//, '').replace(/-/g, ' '),
    });
  }

  for (const path of getAllDynamicGuideCanonicalPaths()) {
    const slug = path.replace(/^\/guides\//, '');
    const guide = getSpecialGuideBySlug(slug);
    entries.push({
      path,
      kind: 'guide',
      label: guide?.navLabel ?? slug,
    });
  }

  for (const post of BLOG_POST_LINK_CORPUS) {
    entries.push({
      path: `/blog/${post.slug}`,
      kind: 'blog',
      label: post.title,
    });
  }

  for (const page of getEnabledExtraKeywordPages(config?.extraKeywordPages)) {
    entries.push({
      path: page.path,
      kind: 'keyword',
      label: page.keyword || page.h1 || page.title || page.path,
    });
  }

  return entries;
}

/** Configured default og:image path for homepage/hub fallbacks. */
export function resolveConfiguredDefaultOgImagePath(
  config?: Pick<PublicMarketingConfig, 'defaultOgImageUrl'>,
): string {
  return resolveDefaultMarketingOgImagePath(config?.defaultOgImageUrl);
}

/** Default og:image path for a marketing route (relative site path). */
export function resolveMarketingOgImagePath(path: string, defaultOgImagePath?: string): string {
  const fallback = defaultOgImagePath?.trim() || resolveDefaultMarketingOgImagePath();
  if (path === '/' || HUB_PAGE_SEO_LABELS[path]) {
    return fallback;
  }

  const special = SPECIAL_STATIC_OG_IMAGES[path];
  if (special) return special;

  if (path.startsWith('/guides/')) {
    const guide = getSpecialGuideBySlug(path.slice('/guides/'.length));
    if (guide?.heroImageUrl) return guide.heroImageUrl;
    return fallback;
  }

  const serviceSlug = path.replace(/^\//, '');
  const serviceCfg = SERVICE_PAGE_CONFIGS[serviceSlug];
  if (serviceCfg) {
    return serviceCfg.images?.hero || serviceCfg.images?.gallery?.[0] || DEFAULT_SERVICE_HERO;
  }

  const locationCfg = Object.values(locationConfigs).find((item) => `/${item.slug}` === path);
  if (locationCfg?.heroImage) return locationCfg.heroImage;

  return fallback;
}

export function resolveMarketingOgImageUrl(
  path: string,
  websiteUrl?: string,
  defaultOgImagePath?: string,
  logoUrl?: string,
): string {
  const resolvedDefault = resolveDefaultMarketingOgImagePath(defaultOgImagePath, { logoUrl });
  return toAbsoluteSiteUrl(
    resolveMarketingOgImagePath(path, resolvedDefault),
    resolveMarketingSiteOrigin(websiteUrl),
  );
}

function isOverrideSource(source: MarketingSeoPreviewSource): boolean {
  return source !== 'corpus';
}

/** Full tenant sitemap audit: canonical URL, resolved SEO, og:image for every marketing route. */
export function auditMarketingSitemap(
  config: PublicMarketingConfig,
  branding: MarketingSeoPreviewBranding,
): MarketingSitemapAuditRow[] {
  const siteOrigin = resolveMarketingSiteOrigin(branding.websiteUrl);
  const defaultOgImagePath = resolveDefaultMarketingOgImagePath(config.defaultOgImageUrl, {
    logoUrl: branding.logoUrl,
  });

  return listAllMarketingSitemapPaths(config).map((entry) => {
    const preview = resolveMarketingSeoPreviewForPath(entry.path, config, branding);
    const source = preview?.source ?? 'corpus';
    return {
      path: entry.path,
      kind: entry.kind,
      label: entry.label,
      canonicalUrl: resolveMarketingSeoPreviewCanonicalUrl(entry.path, branding.websiteUrl),
      ogImageUrl: toAbsoluteSiteUrl(resolveMarketingOgImagePath(entry.path, defaultOgImagePath), siteOrigin),
      title: preview?.title ?? entry.label,
      description: preview?.description ?? '',
      source,
      hasOverride: isOverrideSource(source),
    };
  });
}

export function summarizeMarketingSitemapAudit(rows: MarketingSitemapAuditRow[]): {
  total: number;
  overrides: number;
  byKind: Record<MarketingSitemapPathKind, number>;
} {
  const byKind: Record<MarketingSitemapPathKind, number> = {
    homepage: 0,
    hub: 0,
    service: 0,
    location: 0,
    special: 0,
    guide: 0,
    blog: 0,
    keyword: 0,
  };
  let overrides = 0;
  for (const row of rows) {
    byKind[row.kind] += 1;
    if (row.hasOverride) overrides += 1;
  }
  return { total: rows.length, overrides, byKind };
}

export function serializeMarketingSitemapAuditCsv(rows: MarketingSitemapAuditRow[]): string {
  const header = 'path,kind,label,canonical_url,og_image_url,title,description,source,has_override';
  const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const lines = rows.map((row) =>
    [
      row.path,
      row.kind,
      row.label,
      row.canonicalUrl,
      row.ogImageUrl,
      row.title,
      row.description,
      row.source,
      row.hasOverride ? 'yes' : 'no',
    ]
      .map(escape)
      .join(','),
  );
  return [header, ...lines].join('\n');
}

export function serializeMarketingSitemapAuditJson(rows: MarketingSitemapAuditRow[]): string {
  return JSON.stringify(rows, null, 2);
}
